import { AppKitNetwork, defineChain } from '@reown/appkit/networks'
import {
  createAppKit,
  useAppKit,
  useAppKitAccount,
  useAppKitBalance,
  useAppKitEvents,
  useAppKitNetwork,
  useDisconnect,
} from '@reown/appkit/react'
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import { useQuery } from '@tanstack/react-query'
import {
  JsonRpcSigner,
  parseUnits,
  TransactionReceipt,
  TransactionRequest,
  TransactionResponse,
} from 'ethers'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Account, Chain, Client, http } from 'viem'
import {
  fallback,
  Transport,
  useClient,
  useWalletClient,
  WagmiProvider,
} from 'wagmi'

import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import { config } from '@/config'
import { UiButton } from '@/ui/UiButton'

import { getSignerOrProvider } from './helpers/providers'

const projectId = config.VITE_REOWN_PROJECT_ID

const metadata = {
  name: 'AppKit',
  description: 'AppKit Example',
  url: 'https://app.bitswapp.net', // origin must match your domain & subdomain
  icons: ['https://app.bitswapp.net/branding/favicon.ico'],
}

const bridgelessTestnet = defineChain({
  id: 2607,
  name: 'Bridgeless | Testnet',
  nativeCurrency: {
    name: 'BRIDGE',
    symbol: 'BRIDGE',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['https://eth-rpc.node1.testnet.bridgeless.com'],
    },
  },
  blockExplorers: {
    default: {
      name: 'Bridgless Explorer',
      url: 'https://explorer.testnet.bridgeless.com',
    },
  },
  chainNamespace: 'eip155',
  caipNetworkId: 'eip155:2607',
})

const bridgelessMainnet = defineChain({
  id: 13441,
  name: 'Bridgeless | Mainnet',
  nativeCurrency: {
    name: 'BRIDGE',
    symbol: 'BRIDGE',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['https://eth-rpc.node0.mainnet.bridgeless.com'],
    },
  },
  blockExplorers: {
    default: {
      name: 'Bridgless Explorer',
      url: 'https://explorer.mainnet.bridgeless.com',
    },
  },
  chainNamespace: 'eip155',
  caipNetworkId: 'eip155:13441',
})

export const bridgelessNetwork =
  config.MODE === 'production' ? bridgelessMainnet : bridgelessTestnet

const networks: [AppKitNetwork, ...AppKitNetwork[]] = [bridgelessNetwork]

const wagmiAdapter = new WagmiAdapter({
  networks,
  projectId,
  transports: {
    [bridgelessNetwork.id]: fallback([
      http(bridgelessNetwork.rpcUrls.default.http[0], {
        name: bridgelessNetwork.name,
      }),
    ]),
  },
})

createAppKit({
  adapters: [wagmiAdapter],
  networks: networks,
  projectId,
  metadata,
  features: {
    analytics: false,
    socials: false,
    email: false,
    history: false,
  },
  allWallets: 'HIDE',
})

export type Web3ProviderContext = {
  address: string
  chain: AppKitNetwork | undefined
  chainId: string | undefined
  isConnected: boolean
  balance: bigint

  isInitialized: boolean

  isRightNetwork: boolean

  client: Client<Transport, Chain, Account> | undefined

  supportedChains: AppKitNetwork[]

  signerOrProvider: ReturnType<typeof getSignerOrProvider> | undefined

  connect: () => Promise<void>
  disconnect: () => void
  switchNetwork: (network: AppKitNetwork) => void

  signAndSendTransaction: (
    txRequest: TransactionRequest,
    opts?: {
      onTxSent?: (tx: TransactionResponse) => Promise<void>
    },
  ) => Promise<TransactionReceipt>
}

const web3ProviderContext = createContext<Web3ProviderContext>({
  address: '',
  chain: undefined,
  chainId: undefined,
  isConnected: false,
  balance: 0n,

  isInitialized: false,

  isRightNetwork: false,

  client: undefined,

  supportedChains: [],

  signerOrProvider: undefined,

  connect: async () => {},
  disconnect: () => {},
  switchNetwork: () => {},

  signAndSendTransaction: async () => {
    throw new TypeError('Not implemented')
  },
})

export const useWeb3Context = () => {
  return useContext(web3ProviderContext)
}

export const Web3ContextProvider = (props: PropsWithChildren) => {
  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig}>
      <Web3ContextProviderContent>{props.children}</Web3ContextProviderContent>
    </WagmiProvider>
  )
}

const Web3ContextProviderContent = (props: PropsWithChildren) => {
  const [isInitialized, setIsInitialized] = useState(false)

  const appKitEvent = useAppKitEvents()

  const { open } = useAppKit()

  const { chainId, switchNetwork } = useAppKitNetwork()

  const { isConnected, address, status } = useAppKitAccount({
    namespace: 'eip155',
  })

  const { disconnect } = useDisconnect()

  const client = useClient()

  const { data: walletClient } = useWalletClient({
    config: wagmiAdapter.wagmiConfig,
  })

  const supportedChains = useMemo<AppKitNetwork[]>(() => {
    return wagmiAdapter.wagmiConfig.chains.map(el =>
      defineChain({
        name: el.name,
        nativeCurrency: el.nativeCurrency,
        rpcUrls: el.rpcUrls,
        id: el.id,
        chainNamespace: 'eip155',
        caipNetworkId: `eip155:${el.id}`,
        blockExplorers: el.blockExplorers,
      }),
    )
  }, [])

  const chain = useMemo(() => {
    return supportedChains.find(el => el.id === Number(chainId))
  }, [chainId, supportedChains])

  const { fetchBalance } = useAppKitBalance()
  const { data: balance } = useQuery({
    initialData: 0n,
    queryKey: ['balance', address, chain?.nativeCurrency?.decimals],
    queryFn: async () => {
      if (!chainId || !address) return 0n

      const { data } = await fetchBalance()

      if (!data?.balance) return 0n

      return parseUnits(data.balance, chain?.nativeCurrency?.decimals)
    },
  })

  const isRightNetwork = useMemo((): boolean => {
    return wagmiAdapter.wagmiConfig.chains
      .map(el => el.id)
      .includes(Number(chainId))
  }, [chainId])

  const connect = async () => {
    await open({ view: 'Connect' })
  }

  const signerOrProvider = useMemo(() => {
    return getSignerOrProvider(
      walletClient || (client as Client<Transport, Chain>),
    )
  }, [client, walletClient])

  const signAndSendTransaction = useCallback<
    Web3ProviderContext['signAndSendTransaction']
  >(
    async (txRequest, opts) => {
      if (!signerOrProvider) throw new TypeError('Signer or provider not found')

      if (!(signerOrProvider instanceof JsonRpcSigner))
        throw new TypeError('Signer or provider is not a JsonRpcSigner')

      const tx = await signerOrProvider.sendTransaction({
        ...txRequest,
        ...(!txRequest.from && { from: address }),
      })

      await opts?.onTxSent?.(tx)

      const receipt = await tx.wait()

      if (!receipt) {
        throw new TypeError('Transaction receipt not found')
      }

      return receipt
    },
    [address, signerOrProvider],
  )

  useEffect(() => {
    if (
      appKitEvent?.data.event === 'CONNECT_SUCCESS' ||
      appKitEvent?.data.event === 'INITIALIZE'
    ) {
      setIsInitialized(true)
    }
  }, [appKitEvent, status])

  if (!isInitialized) return null

  return (
    <web3ProviderContext.Provider
      value={{
        address: address ?? '',
        chain,
        chainId: String(chainId),
        isConnected: isConnected,
        balance,

        isInitialized,

        isRightNetwork,

        client: walletClient,

        supportedChains,

        signerOrProvider,

        connect,
        disconnect,
        switchNetwork,

        signAndSendTransaction,
      }}
    >
      {props.children}

      <ResponsiveModalDrawer
        open={
          isConnected && !supportedChains.find(el => el.id === Number(chainId))
        }
        title='Switch to supported chain'
        content={
          <div className='flex items-center justify-center gap-2'>
            <UiButton
              onClick={async () => {
                walletClient?.addChain({
                  chain: supportedChains[0] as Chain,
                })
              }}
            >
              Switch to {supportedChains[0].name}
            </UiButton>
          </div>
        }
      />
    </web3ProviderContext.Provider>
  )
}
